import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FAQ from '../components/FAQ';
import ContactPopup from '../components/ContactPopup';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, Users, BookOpen } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Popup state management
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Get in Touch",
    subtitle: "Connect with our admissions team to learn more about our programs",
    formType: "popup_inquiry",
    interestedProgram: ""
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Get in Touch",
      subtitle: config.subtitle || "Connect with our admissions team to learn more about our programs",
      formType: config.formType || "popup_inquiry",
      interestedProgram: config.interestedProgram || ""
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // Prepare form data for webhook
      const webhookData = {
        // Standard contact form fields
        full_name: formData.name,
        email: formData.email,
        subject: formData.subject || 'Contact Form Submission',
        message: formData.message,
        
        // Metadata for tracking
        form_type: 'contact_form',
        page_source: 'contact_page',
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || 'direct'
      };

      // Send to webhook
      const response = await fetch('https://hook.us1.make.com/kufviv689kkio8ys9rpipb0g4qyfquro', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (response.ok) {
        // Success - clear form and show success message
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setSubmitStatus('success');
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending form data:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F1EFED' }}>
      <Header openPopup={openPopup} />
      
      {/* Hero Section */}
      <div className="py-16 sm:py-20" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              <span className="text-blue-600">Contact</span> Us
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Ready to transform your career? We're here to guide you every step of the way on your journey to becoming a 10X professional.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">

        <div className="grid lg:grid-cols-2 gap-16 xl:gap-20">
          {/* Contact Information */}
          <div className="space-y-10">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 tracking-tight">Multiple Ways to <span className="text-blue-600">Connect</span></h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Whether you have questions about our programs, need technical support, or want to discuss your career transformation goals, we're here to help. Choose the method that works best for you.
              </p>
            </div>

            <div className="space-y-10">
              {/* Business Identity */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center space-x-4 mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Business <span className="text-blue-600">Identity</span></h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">1to10x</span> is a public-facing brand name used for programs and services offered through this website. <span className="font-semibold text-gray-900">ANM ENABLERS PRIVATE LIMITED</span> and <span className="font-semibold text-gray-900">1to10X LLC</span> provide such programs and services, as applicable, for their respective operations and obligations.
                </p>
              </div>

              {/* Email */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center space-x-4 mb-4">
                  <Mail className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Email <span className="text-blue-600">Support</span></h3>
                </div>
                <p className="text-gray-600 mb-3">Get detailed answers to your questions</p>
                <a 
                  href="mailto:support@1to10x.com" 
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                >
                  support@1to10x.com
                </a>
              </div>

              {/* Phone Numbers */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center space-x-4 mb-4">
                  <Phone className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Phone <span className="text-green-600">Support</span></h3>
                </div>
                <p className="text-gray-600 mb-4">Speak directly with our team</p>
                
                <div className="grid gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">United States</h4>
                    <a 
                      href="tel:+12128146967" 
                      className="text-green-600 hover:text-green-700 font-medium transition-colors duration-200"
                    >
                      +1 (212) 814-6967
                    </a>
                  </div>
                </div>
              </div>

              {/* Office Locations */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center space-x-4 mb-4">
                  <MapPin className="w-6 h-6 text-red-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Our <span className="text-red-600">Offices</span></h3>
                </div>
                
                <div className="space-y-4">
                  {/* San Francisco Office */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">San Francisco, USA</h4>
                    <p className="text-gray-600">
                      166 Geary St, San Francisco<br />
                      California 94108, United States
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg p-8 border border-gray-200" id="contact-form">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Send us a <span className="text-blue-600">Message</span></h2>
              <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours during business days.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-base font-semibold text-gray-800 mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-base"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-base font-semibold text-gray-800 mb-3">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-base"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-base font-semibold text-gray-800 mb-3">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-base"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-base font-semibold text-gray-800 mb-3">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-vertical bg-white text-gray-900 placeholder-gray-500 text-base"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-green-800 font-semibold">Thank you for reaching out!</p>
                      <p className="text-green-600 text-sm">We've received your message and will get back to you within 24 hours.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-red-800 font-semibold">Failed to send message</p>
                      <p className="text-red-600 text-sm">Please try again or contact us directly at support@1to10x.com</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Default Message */}
              {submitStatus === 'idle' && (
                <div className="text-center">
                  {/* <p className="text-sm text-gray-500">
                    We'll get back to you within 24 hours during business days.
                  </p> */}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Support Categories */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How Can We <span className="text-blue-600">Help</span> You?</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Choose the type of support you need and we'll connect you with the right team member.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors duration-200">
              <div className="flex items-center space-x-3 mb-4">
                <BookOpen className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Program <span className="text-blue-600">Information</span></h3>
              </div>
              <p className="text-gray-600 mb-4">Get detailed information about our course structure, curriculum, duration, and enrollment requirements.</p>
              <a href="mailto:programs@1to10x.com" className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                programs@1to10x.com
              </a>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors duration-200">
              <div className="flex items-center space-x-3 mb-4">
                <MessageCircle className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Technical <span className="text-green-600">Support</span></h3>
              </div>
              <p className="text-gray-600 mb-4">Need help with platform access, technical issues, or troubleshooting? Our tech team is here to help.</p>
              <a href="mailto:support@1to10x.com" className="text-green-600 hover:text-green-700 font-medium transition-colors duration-200">
                support@1to10x.com
              </a>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors duration-200">
              <div className="flex items-center space-x-3 mb-4">
                <Users className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Career <span className="text-purple-600">Guidance</span></h3>
              </div>
              <p className="text-gray-600 mb-4">Discuss your career goals, learning path, and professional growth plan with our team.</p>
              <a href="mailto:careers@1to10x.com" className="text-purple-600 hover:text-purple-700 font-medium transition-colors duration-200">
                careers@1to10x.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <FAQ />

      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
  );
};

export default Contact; 
